import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { Box, FormControl, FormLabel, Input, Button, Alert, Divider, Typography, Tabs, TabList, Tab, TabPanel } from '@mui/joy';
import AuthCard from '../components/auth/AuthCard';
import CodeLoginForm from '../components/auth/CodeLoginForm';
import DoctorLoginConfirmation from '../components/auth/DoctorLoginConfirmation';
import PsychologyRounded from '@mui/icons-material/PsychologyRounded';
import { LayoutContext } from '../context';
import { apiDoctorLogin } from '../auth';
import { formatPhoneRu } from '../utils';
import PasswordResetForm from '../components/auth/PasswordResetForm';
import ConsentCheckbox from '../components/auth/ConsentCheckbox';


export default function LoginDoctorPage() {
  const navigate = useNavigate();
  const { applyLogin, isVerified, account } = useContext(LayoutContext);

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authTab, setAuthTab] = useState('password');
  const [resetMode, setResetMode] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const loginPending = useRef(false);
  const pageEpoch = useRef(0);
  useEffect(() => { ++pageEpoch.current; return () => { ++pageEpoch.current; }; }, []);
  const changeConsent = (value) => {
    setConsentAccepted(value);
    setError((previous) => previous === 'Подтвердите согласие на обработку персональных данных' ? '' : previous);
  };

  const onSubmit = async () => {
    if (loginPending.current) return;
    setError('');
    if (!consentAccepted) { setError('Подтвердите согласие на обработку персональных данных'); return; }
    if (!phone || !password) { setError('Введите телефон и пароль'); return; }
    const generation = pageEpoch.current;
    loginPending.current = true; setLoading(true);
    try {
      const resp = await apiDoctorLogin({ phone, password, device_name: navigator.userAgent, consentAccepted });
      if (generation !== pageEpoch.current) return;
      if (!resp?.success || !resp.requires_confirmation || !resp.challenge) {
        setError(resp?.error || 'Сервер не поддерживает подтверждение входа. Обновите сервер.'); return;
      }
      setPassword('');
      setChallenge({ value: resp.challenge, masked: resp.destination_masked });
    } catch (e) {
      if (generation === pageEpoch.current) setError(e?.response?.data?.error || 'Не удалось войти');
    } finally {
      if (generation === pageEpoch.current) { loginPending.current = false; setLoading(false); }
    }
  };

  const onCodeSuccess = (profile) => {
    applyLogin(profile);
    navigate('/', { replace: true });
  };

  return (
    <AuthCard
      icon={<PsychologyRounded sx={{ fontSize: 30, color: 'white' }} />}
      title="Вход для врача"
      subtitle={challenge ? "Подтвердите вход на телефон врача" : "Войдите по телефону и паролю"}
      maxWidth={400}
      footer={
        <>
          <Divider />
          <Button
            variant="plain" color="neutral" size="sm"
            component={Link} to="/login/patient"
            sx={{ alignSelf: 'center' }}
          >
            Войти как пациент
          </Button>
        </>
      }
    >
      {challenge ? (
        <DoctorLoginConfirmation key={challenge.value} challenge={challenge.value} masked={challenge.masked}
          consentAccepted={consentAccepted} onConsentChange={changeConsent} onSuccess={onCodeSuccess}
          onCancel={() => { setChallenge(null); setPassword(''); setError(''); }} />
      ) : (
      <Tabs value={authTab} onChange={(e, v) => { if (!loading && v) setAuthTab(v); }} sx={{ bgcolor: 'transparent' }}>
        <TabList
          sx={{
            position: 'relative',
            mb: 2, p: '4px', gap: 0,
            borderRadius: '12px',
            bgcolor: 'rgba(64,61,132,0.07)',
            '& button': {
              flex: 1, position: 'relative', zIndex: 1,
              minHeight: 0, py: 0.75,
              fontSize: '14px', fontWeight: 'lg', whiteSpace: 'nowrap',
              color: 'text.tertiary', bgcolor: 'transparent',
              transition: 'color .25s ease',
              '&:hover': { color: 'text.secondary', bgcolor: 'transparent' },
              '&[aria-selected="true"]': { color: '#403D84' },
            },
          }}
        >
          {/* скользящая «таблетка» активной вкладки */}
          <Box aria-hidden sx={{
            position: 'absolute', top: 4, left: 4, zIndex: 0,
            width: 'calc((100% - 8px) / 3)', height: 'calc(100% - 8px)',
            borderRadius: '9px', bgcolor: '#fff',
            boxShadow: '0 6px 16px -8px rgba(64,61,132,0.45)',
            transform: `translateX(${['password', 'phone', 'email'].indexOf(authTab) * 100}%)`,
            transition: 'transform .32s cubic-bezier(.34,1.2,.4,1)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }} />

          <Tab value="password" disabled={loading} variant="plain" disableIndicator>Пароль</Tab>
          <Tab value="phone" disabled={loading} variant="plain" disableIndicator>Телефон</Tab>
          <Tab value="email" disabled={loading} variant="plain" disableIndicator>Почта</Tab>
        </TabList>

        <TabPanel value="password" sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {resetMode ? (
            <PasswordResetForm role="doctor" consentAccepted={consentAccepted} onConsentChange={changeConsent} onSuccess={onCodeSuccess} onCancel={() => setResetMode(false)} />
          ) : (
            <>
              {error && <Alert color="danger" variant="soft">{error}</Alert>}

              <FormControl>
                <FormLabel>Телефон</FormLabel>
                <Input
                  type="tel"
                  placeholder="+7 (999) 123-45-67"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneRu(e.target.value, phone))}
                  onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
                  slotProps={{ input: { inputMode: 'tel', maxLength: 18 } }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Пароль</FormLabel>
                <Input
                  type="password"
                  placeholder="Пароль"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
                />
              </FormControl>

              <Button type="button" loading={loading} disabled={!consentAccepted} onClick={onSubmit} sx={{ mt: 0.5 }}>
                Войти
              </Button>
              <ConsentCheckbox checked={consentAccepted} onChange={changeConsent} disabled={loading} />

              <Button variant="plain" size="sm" onClick={() => setResetMode(true)} sx={{ alignSelf: 'center' }}>
                Забыли пароль?
              </Button>

              <Typography level="body-xs" textColor="text.tertiary" sx={{ textAlign: 'center' }}>
                Пароль выдаёт администратор клиники
              </Typography>
            </>
          )}
        </TabPanel>

        <TabPanel value="phone" sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CodeLoginForm role="doctor" mode="phone" consentAccepted={consentAccepted} onConsentChange={changeConsent} onSuccess={onCodeSuccess} />
        </TabPanel>

        <TabPanel value="email" sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CodeLoginForm role="doctor" mode="email" consentAccepted={consentAccepted} onConsentChange={changeConsent} onSuccess={onCodeSuccess} />
        </TabPanel>
      </Tabs>
      )}
    </AuthCard>
  );
}